import { Button, Input, message, Table } from 'antd'
import { ColumnsType, SorterResult, SortOrder } from 'antd/es/table/interface'
import { Breakpoint } from 'antd/lib/_util/responsiveObserver'
import React, { FunctionComponent, useCallback, useMemo, useState } from 'react'

import { MonitoringInputType } from '../../../common/cuzkMonitorTypes'
import { monitoringInputLimit } from '../../utils/layoutConst'
import { CeeMonitorAddSubjectModal } from '../CeeMonitorAddSubjectModal/CeeMonitorAddSubjectModal'
import { CuzkMonitorAddSubjectModal } from '../CuzkMonitorAddSubjectModal/CuzkMonitorAddSubjectModal'
import { RcTableItem } from '../CuzkMonitorRcTable/CuzkMonitorRcTable'
import { Fade } from '../Fade/Fade'
import { TabbleCellEditNoteModal } from '../TabbleCellEditNoteModal/TabbleCellEditNoteModal'

export interface NameBirthdateBulkTableItem
  extends Omit<RcTableItem, 'createdAt' | 'rc' | 'labels'> {
  key: number
  birthDate: string
  firstname: string
  lastname: string
}

export interface NameBirthdateBulkInputProps {
  type: MonitoringInputType
  buttonRef: React.MutableRefObject<null>
  inputRef: React.MutableRefObject<null>
  monitoring?: boolean
}

const locale = {
  emptyText: 'Vložte údaje do hromadného importu (jeden subjekt na řádek).',
}

const normalizeCuzkBirthDate = (value: string): string => {
  value = value
    .replace(/[-/]/g, '.')
    .replace(/[^\d.]+/g, '')
    .replace(/\.{2,}/g, '.')

  if (value.length === 8 && /^[0-9]+$/.test(value)) {
    return value.slice(0, 2) + '.' + value.slice(2, 4) + '.' + value.slice(4)
  }

  const parts = value.split('.')
  return parts
    .map((part, index) => (index < 2 && part.length === 1 ? '0' + part : part))
    .join('.')
}

const normalizeCeeBirthDate = (value: string): string => {
  value = value
    .replace(/[-/]/g, '.')
    .replace(/[^\d.]+/g, '')
    .replace(/\.{2,}/g, '.')

  if (value.length === 8 && /^[0-9]+$/.test(value)) {
    return value.slice(0, 1) + '.' + value.slice(1, 2) + '.' + value.slice(2)
  }

  const parts = value.split('.')
  return parts
    .map((part, index) => {
      if (index < 2 && part.startsWith('0')) {
        return part.substring(1)
      }
      return part
    })
    .join('.')
}

export const NameBirthdateBulkInput: FunctionComponent<NameBirthdateBulkInputProps> =
  props => {
    const [bulkInputValue, setBulkInputValue] = useState('')
    const [, setSortedInfo] = useState<
      SorterResult<NameBirthdateBulkTableItem>
    >({})
    const [dataSource, setDataSource] = useState<NameBirthdateBulkTableItem[]>(
      []
    )

    const isBirthDateValid = useCallback(
      (value: string): boolean => {
        const parts = value ? value.split('.') : []
        if (parts.length !== 3) return false
        const day = parseInt(parts[0])
        const month = parseInt(parts[1])
        const year = parseInt(parts[2])

        if (Number.isNaN(day) || Number.isNaN(month) || Number.isNaN(year)) {
          return false
        }
        if (day < 1 || day > 31) return false
        if (month < 1 || month > 12) return false
        if (year < 1910 || year > new Date().getFullYear()) return false

        if (props.type === MonitoringInputType.CuzkName) {
          return /^(\d{2})\.(\d{2})\.(\d{4})$/.test(value)
        }

        if (props.type === MonitoringInputType.CeeName) {
          return /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/.test(value)
        }

        return false
      },
      [props.type]
    )

    const normalizeBirthDate = useCallback(
      (raw: string) => {
        if (props.type === MonitoringInputType.CuzkName) {
          return normalizeCuzkBirthDate(raw)
        }
        if (props.type === MonitoringInputType.CeeName) {
          return normalizeCeeBirthDate(raw)
        }
        return ''
      },
      [props.type]
    )

    const deleteItem = useCallback(
      (key: number) => {
        setDataSource(prev => prev.filter(obj => obj.key !== key))
        message.success('Položka byla odebrána.')
      },
      [setDataSource]
    )

    const editNote = useCallback(
      (id: number, note?: string) => {
        setDataSource(prevDataSource => {
          const index = prevDataSource.findIndex(item => item.key === id)
          if (index === -1) return prevDataSource
          const newDataSource = [...prevDataSource]
          newDataSource[index] = { ...newDataSource[index], description: note }
          return newDataSource
        })
      },
      [setDataSource]
    )

    const columns = useMemo<ColumnsType<NameBirthdateBulkTableItem>>(
      () => [
        {
          title: 'ID',
          dataIndex: 'key',
          key: 'key',
          sorter: {
            compare: (a: { key: number }, b: { key: number }) => a.key - b.key,
            multiple: 1,
          },
          defaultSortOrder: 'descend' as SortOrder,
        },
        {
          title: 'Poznámka',
          dataIndex: 'description',
          key: 'description',
          render: (text: string, record) => (
            <TabbleCellEditNoteModal
              onSave={editNote}
              initialNote={text}
              recordId={record.key}
            />
          ),
        },
        {
          title: 'Jméno',
          dataIndex: 'fullName',
          key: 'fullName',
          render: (_, record) =>
            (record.firstname ? record.firstname : '') +
            ' ' +
            (record.lastname ? record.lastname : ''),
        },
        {
          title: 'Datum narození',
          dataIndex: 'birthDate',
          key: 'birthDate',
        },
        {
          title: 'Kód validace',
          dataIndex: 'validatedCode',
          key: 'validatedCode',
          responsive: ['' as Breakpoint],
        },
        {
          title: 'Akce',
          dataIndex: 'actions',
          key: 'actions',
          render: (_, record) => (
            <Button type="link" danger onClick={() => deleteItem(record.key)}>
              Odebrat
            </Button>
          ),
        },
      ],
      [deleteItem, editNote]
    )

    const parseBulkLine = useCallback(
      (rawLine: string) => {
        const line = rawLine.trim()
        if (!line) return null

        const parts = line.split(':')
        if (parts.length < 3) {
          return {
            firstname: '',
            lastname: '',
            birthDate: '',
            note: '',
            validatedCode: 2,
            typed: line,
          }
        }

        const firstname = (parts[0] || '').trim()
        const lastname = (parts[1] || '').trim()
        const rawBirthDate = (parts[2] || '').trim()
        const note = parts.slice(3).join(':').trim()

        const birthDate = normalizeBirthDate(rawBirthDate)

        const validatedCode =
          firstname && lastname && birthDate && isBirthDateValid(birthDate)
            ? 30
            : 2

        return {
          firstname,
          lastname,
          birthDate,
          note,
          validatedCode,
          typed: line,
        }
      },
      [isBirthDateValid, normalizeBirthDate]
    )

    const handleBulkChange = useCallback(
      (event: { target: { value: string } }) => {
        const value = event.target.value
        setBulkInputValue(value)

        // Only parse when the user confirms at least one full line (Enter) or pastes multiple lines.
        if (!/[\n]+/.test(value)) return

        const lines = value
          .split(/[\n]+/)
          .map(v => v.trim())
          .filter(v => v.length > 0)

        if (!lines.length) return

        setDataSource(prev => {
          const next = [...prev]

          lines.forEach(rawLine => {
            const parsed = parseBulkLine(rawLine)
            if (!parsed) return

            const idBase =
              (parsed.firstname || '') +
              (parsed.lastname || '') +
              (parsed.birthDate || '')

            const isDuplicate = next.some(
              item =>
                (item.firstname || '') +
                  (item.lastname || '') +
                  (item.birthDate || '') ===
                idBase
            )

            const finalValidatedCode = isDuplicate ? 5 : parsed.validatedCode

            const newKey = next.length
              ? (next[next.length - 1]?.key || 0) + 1
              : 1

            next.push({
              id: idBase || rawLine,
              key: newKey,
              firstname: parsed.firstname,
              lastname: parsed.lastname,
              birthDate: parsed.birthDate,
              description: parsed.note,
              validatedCode: finalValidatedCode,
              typed: parsed.typed,
            })
          })

          return next
        })

        setSortedInfo({
          order: 'ascend',
          columnKey: 'validatedCode',
        })

        setBulkInputValue('')
        lines.length > 1
          ? message.success('Přidáno ' + lines.length + ' záznamů')
          : message.success('Záznam přidán')
      },
      [parseBulkLine]
    )

    return (
      <>
        <div className="w-full flex items-start space-x-2" ref={props.inputRef}>
          <Input.TextArea
            placeholder="Jeden subjekt na řádek: křestní jméno:příjmení:datum narození:poznámka volitelná (Enter pro potvrzení)"
            value={bulkInputValue}
            onChange={handleBulkChange}
            maxLength={monitoringInputLimit}
            autoSize
            size="large"
          />
          <div ref={props.buttonRef}>
            {props.type === MonitoringInputType.CuzkName ? (
              <CuzkMonitorAddSubjectModal dataSource={dataSource} />
            ) : (
              <CeeMonitorAddSubjectModal dataSource={dataSource} />
            )}
          </div>
        </div>

        <Fade show={dataSource.length > 0}>
          <Table
            className="mt-4"
            dataSource={[...dataSource]}
            columns={columns}
            bordered
            size="small"
            rowClassName="animate-fadeIn"
            locale={locale}
          />
        </Fade>
      </>
    )
  }
